import styled from '@emotion/styled';
import type { FieldProps, TextInputProps } from '@hover/blueprint';
import {
  Field,
  TextInput,
  forwardRef,
  useMergeRefs,
  useTheme,
} from '@hover/blueprint';
import type { IconProp } from '@hover/icons';
import type { ReactElement } from 'react';
import React from 'react';
import { useFormContext } from 'react-hook-form';

interface TextFieldProps
  extends Omit<FieldProps, 'children'>,
    Pick<TextInputProps, 'iconBefore' | 'placeholder' | 'type'> {
  iconBefore?: IconProp;
  elementAfter?: ReactElement;
}

const StyledField = styled(Field)`
  & > div {
    color: ${props => (props.error ? '#c20b0b' : 'inherit')};
    font-size: 12px;
  }
`;

const ElementAfterWrapper = styled.div`
  position: absolute;
  right: 8px;
  top: 8px;
`;

const StyledTextInput = styled(TextInput)`
  color: ${({ theme }) => theme.colors.neutral900} !important;
  height: 65px !important;
  background-color: ${({ theme }) => theme.colors.neutral100} !important;
  padding: 10px 20px 10px 20px !important;
  border-radius: 20px !important;
  border-color: ${({ theme }) => theme.colors.neutral100} !important;
  &:hover,
  &:focus {
    border: 2px solid ${({ theme }) => theme.colors.neutral900} !important;
    outline: none !important;
    box-shadow: none !important;
  }
  &:placeholder-shown {
    color: ${({ theme }) => theme.colors.neutral600} !important;
  }
  &[aria-invalid='true'] {
    border: 1.5px solid #c20b0b !important;
    &:hover,
    &:focus {
      border-width: 2.5px !important;
    }
  }
`;

const NewTextField = forwardRef<TextFieldProps, 'input'>(
  ({ iconBefore, elementAfter, name, placeholder, type, ...props }, ref) => {
    // eslint-disable-next-line @typescript-eslint/unbound-method
    const { errors, register } = useFormContext<Record<string, string>>();
    const refs = useMergeRefs(ref, register);
    const theme = useTheme();
    const error = errors[name]?.message;

    return (
      <StyledField {...props} error={error} name={name} pb={0}>
        <StyledTextInput
          elementAfter={
            elementAfter && (
              <ElementAfterWrapper>{elementAfter}</ElementAfterWrapper>
            )
          }
          iconBefore={iconBefore}
          placeholder={placeholder}
          ref={refs}
          size="default"
          theme={theme}
          type={type}
        />
      </StyledField>
    );
  },
);

export { NewTextField };
